<template>
    <div class="d-flex flex-column" style="height: 100%; min-height: 0">
        <!-- Two siblings: content + footer -->

        <!-- Content -->
        <v-container
            class="flex-grow-1 d-flex flex-column"
            style="min-height: 0"
        >
            <!-- Fixed header -->
            <v-row
                v-if="displayedProject"
                class="flex-shrink-0 flex-grow-0 pt-2"
            >
                <v-col>
                    <OngoingProjectDetails
                        :project="displayedProject.project"
                        :color="
                            displayedProject.project?.projectId ===
                            'SYSTEM_LUNCH-BREAK'
                                ? 'warning'
                                : 'primary'
                        "
                        :variant="isEditDescription ? 'flat' : 'elevated'"
                        :custom-styles="{ padding: '20px' }"
                        :disabled="isEditDescription"
                        :click-handler="showEditDescription"
                        :project-name="displayedProject.project?.name"
                        :start-time="displayedProject.startTime"
                        :description="displayedProject.description"
                        @save-description="saveDescription"
                        :isEditable="true"
                    />
                </v-col>
            </v-row>

            <!-- Snackbar Confirmation -->
            <v-overlay v-model="snackbar" opacity="0.5">
                <v-snackbar
                    color="#333"
                    height="150px"
                    v-model="snackbar"
                    class="mb-16"
                    rounded="shaped"
                    :timeout="5000"
                    location="center"
                >
                    {{ snackbarMessage }}
                    <template v-slot:actions>
                        <v-btn
                            color="error"
                            variant="text"
                            size="x-large"
                            @click="confirmAction"
                        >
                            OK
                        </v-btn>
                    </template>
                </v-snackbar>
            </v-overlay>

            <!-- Scrollable projects grid -->
            <v-row
                class="flex-grow-1"
                style="
                    min-height: 0;
                    overflow-y: auto;
                    scrollbar-width: none;
                    align-content: flex-start;
                "
            >
                <v-col
                    cols="12"
                    :sm="projectsColumnDisplay.sm"
                    :md="projectsColumnDisplay.md"
                    :lg="projectsColumnDisplay.lg"
                    v-for="(project, index) in sortedProjects"
                    :key="project.projectId"
                    style="padding: 10px 12px 0 12px; flex: unset"
                >
                    <v-tooltip
                        :disabled="!shouldShowTooltip(project)"
                        location="top"
                        :model-value="
                            shouldShowTooltip(project) &&
                            showTooltipForProject === project.projectId
                        "
                    >
                        <template #activator="{ props }">
                            <v-btn
                                v-bind="shouldShowTooltip(project) ? props : {}"
                                variant="outlined"
                                block
                                @click="handleProjectClick(project, $event)"
                                @touchstart="handleTouchStart(project, $event)"
                                @touchend="handleTouchEnd(project, $event)"
                                @touchcancel="handleTouchCancel"
                                :disabled="disableProjectSelection"
                                :color="
                                    getProjectPriorityColor(
                                        project.userPriority,
                                        project.globalPriority
                                    )
                                "
                                size="large"
                                class="project-button"
                            >
                                <div class="project-button-content">
                                    <span class="text-ellipsis" style="flex: 1 1 0%;">{{
                                        project.name
                                    }}</span>
                                    <span v-if="project.activeUserCount" class="project-user-count">{{
                                        project.activeUserCount
                                    }}</span>
                                </div>
                            </v-btn>
                        </template>

                        <span style="white-space: pre-line">{{
                            project.activeUserNames
                        }}</span>
                    </v-tooltip>
                </v-col>
            </v-row>
        </v-container>

        <!-- Footer -->
        <BottomButtonWrapper :isShowing="true">
            <v-col cols="6">
                <ThemedActionButton
                    :color="'success'"
                    :text="'End Of Day'"
                    @click="handleEndOfDay"
                    :disabled="disableProjectSelection"
                ></ThemedActionButton>
            </v-col>

            <v-col cols="6">
                <ThemedActionButton
                    :color="'warning'"
                    :text="'Lunch Break'"
                    @click="startLunchBreak"
                    :disabled="isOnLunchBreak || disableProjectSelection"
                >
                </ThemedActionButton>
            </v-col>
        </BottomButtonWrapper>
    </div>
</template>

<script>
import { auth, db } from "@/firebaseConfig";
import { ref, push, update, get, set } from "firebase/database";
import { mapGetters, mapActions, mapMutations } from "vuex";
import OngoingProjectDetails from "./OngoingProjectDetails.vue";
import BottomButtonWrapper from "./BottomButtonWrapper.vue";
import ThemedActionButton from "./ThemedActionButton.vue";
import { getActiveEntry } from "@/utils/utils";

export default {
    name: "OperatorDashboard",
    /**
     * Component's reactive data properties.
     * @returns {Object} An object containing the component's reactive state.
     */
    data() {
        return {
            snackbar: false, // Boolean to control snackbar visibility.
            snackbarMessage: "", // Message displayed in the snackbar.
            snackbarAction: null, // Action callback associated with the snackbar.
            snackbarType: "", // Type of snackbar ('project', 'lunchBreak', 'endOfDay').
            selectedProject: null, // Currently selected project object.
            isDescription: false,
            isEditDescription: false,
            descriptionText: "",
            showCurrentProject: false,
            disableProjectSelection: false,
            // Long press state for mobile tooltips
            longPressTimer: null,
            longPressProjectId: null,
            showTooltipForProject: null, // Project ID to show tooltip for
            longPressTouchEndTime: null, // Timestamp when touchend fired after long press
        };
    },

    /**
     * Components registered in this component.
     */
    components: {
        OngoingProjectDetails,
        BottomButtonWrapper,
        ThemedActionButton,
    },

    /**
     * Computed properties derived from Vuex store or internal data.
     */
    computed: {
        ...mapGetters({
            user: "getUser", // Logged-in user data.
            rights: "getRights",
            projects: "getWorkingProjects", // List of working projects.
            activeProject: "getActiveProject", // Currently active project.
            startTimeGetter: "getStartTime", // Start time from Vuex store.
            isOnLunchBreak: "getIsOnLunchBreak", // Boolean indicating if user is on a lunch break.
        }),
        /**
         * Determines the Vuetify grid column spans (sm, md, lg) based on
         * the total number of projects.
         *
         * If there are fewer than 11 projects, each column will span the full width (12),
         * forcing a single-column layout. Otherwise, columns will span 6,
         * resulting in a two-column layout.
         *
         * @returns {{sm: number, md: number, lg: number}}
         *   An object whose keys are Vuetify breakpoints and whose values are the
         *   number of grid columns to span at that breakpoint.
         */
        projectsColumnDisplay() {
            const count = this.sortedProjects.length;
            if (count < 11) {
                return { sm: 12, md: 12, lg: 12 };
            } else {
                return { sm: 6, md: 6, lg: 6 };
            }
        },
        /**
         * Retrieves the description of the most recent time entry.
         *
         * - Fetches the `lastEntryKey` from the Vuex state.
         * - Safely navigates to the corresponding time entry in the user's data.
         * - Returns the description if it exists and is not empty.
         *
         * @returns {string|null} The description of the time entry if available, otherwise `null`.
         */
        userTimeEntryDescription() {
            const lastEntryKey = this.$store.state.lastEntryKey; // Retrieve lastEntryKey from Vuex state
            if (!lastEntryKey) {
                return null; // No last entry key available
            }

            // Safely navigate to the time entry description
            const timeEntry = this.user?.timeEntries?.[lastEntryKey];

            // Check if the time entry exists and has a non-empty description
            if (
                timeEntry &&
                timeEntry.description &&
                timeEntry.description.trim() !== ""
            ) {
                return timeEntry.description; // Return the description if it's valid
            }

            return null; // Return null if no valid description is found
        },

        /**
         * Sorts projects based on global order and priorities:
         * - Global order is considered first.
         * - If priorities match, user-specific priorities are considered.
         * - Priority sorting follows this order: Highest, High, Normal, Low, Lowest.
         */
        sortedProjects() {
            const priorityOrder = {
                Highest: 1,
                High: 2,
                Normal: 3,
                Low: 4,
                Lowest: 5,
            };
            // Map and sort projects
            const sorted = [...(this.projects || [])]
                .filter((project) => project.status !== "systemProject")
                .map((project) => {
                    const userProject = this.user.projects[project.projectId];
                    return {
                        ...project,
                        globalPriority:
                            priorityOrder[project.priority || "Normal"], // Global priority
                        userPriority:
                            priorityOrder[userProject?.priority || "Normal"], // User-specific priority
                        globalOrder: Number(userProject?.order || 0), // Ensure globalOrder is numeric
                    };
                })
                .sort((a, b) => {
                    // Compare by user priority first
                    if (a.userPriority !== b.userPriority) {
                        return a.userPriority - b.userPriority;
                    }

                    // If user priority matches, fallback to global priority
                    if (a.globalPriority !== b.globalPriority) {
                        return a.globalPriority - b.globalPriority;
                    }

                    // If both priorities match, fallback to global order
                    return a.globalOrder - b.globalOrder;
                })
                .map((project) => {
                    // Add active user count and names
                    const proj = { ...project };
                    Object.defineProperties(proj, {
                        activeUserCount: {
                            get() {
                                return Object.keys(project.activeUsers || {})
                                    .length;
                            },
                            enumerable: false,
                        },
                        activeUserNames: {
                            get() {
                                return Object.values(project.activeUsers || {})
                                    .map((u) =>
                                        `${u?.name || ""} ${
                                            u?.surname || ""
                                        }`.trim()
                                    )
                                    .filter(Boolean)
                                    .join("\n");
                            },
                            enumerable: false,
                        },
                    });

                    return proj; //  Important
                });

            return sorted;
        },

        /**
         * Unified source for the displayed project information.
         * Prioritizes Vuex state (activeProject) when available, falls back to activeEntry-derived project.
         * This ensures consistent display regardless of state synchronization timing.
         * @returns {Object|null} Object with project, startTime, and description, or null if no active project.
         */
        displayedProject() {
            // Priority 1: Use Vuex activeProject if available (most reliable, already synchronized)
            if (this.activeProject) {
                return {
                    project: this.activeProject,
                    startTime: this.startTimeGetter,
                    description: this.userTimeEntryDescription,
                };
            }

            // Priority 2: Fallback to activeEntry-derived project (during state restoration)
            if (this.activeEntry) {
                const project = this.projects.find(
                    (p) => p.projectId === this.activeEntry.projectId
                );
                if (project) {
                    // Set showCurrentProject flag for backward compatibility
                    this.showCurrentProject = true;
                    // Check if the project name is "Lunch Break" and set isOnLunchBreak to true
                    if (project.name === "Lunch Break") {
                        this.setIsOnLunchBreak(true);
                    }
                    return {
                        project,
                        startTime: this.activeEntry.startTime,
                        description: this.activeEntry.description || "",
                    };
                }
            }

            return null;
        },
        /**
         * The user's active time entry, if any.
         * @returns {Object|null} Active time entry object or null if none exists.
         */
        activeEntry() {
            return getActiveEntry(this.user);
        },
    },
    watch: {
        // Watch Vuex store for overtime toggle changes
        "$store.state.overtimeToggleValue"(newValue) {
            if (newValue !== null) {
                this.onOvertimeToggle(newValue);
                // Reset after handling to allow future toggles
                this.$store.commit("setOvertimeToggleValue", null);
            }
        },
    },
    created() {
        const { projectId, projectName } = this.$route.query;
        if (projectId && projectName) {
            const project = { projectId, name: projectName };
            this.startProject(project);
        }

        this.restoreSession();
    },

    /**
     * Called when the route changes but the component is not reloaded.
     * This ensures that if a user navigates to the same route with different query parameters,
     * the new project data is processed correctly.
     *
     * @param {object} to - The target route object being navigated to.
     * @param {object} from - The current route object being navigated away from.
     * @param {Function} next - A function that must be called to resolve the navigation.
     */
    beforeRouteUpdate(to, from, next) {
        const { projectId, projectName } = to.query;
        if (projectId && projectName) {
            const project = { projectId, name: projectName };
            this.startProject(project);
        }
        next(); // Proceed with the route change
    },
    methods: {
        ...mapActions([
            "startProjectsListener",
            "updateUserStatus",
            "endCurrentProject",
            "addRemoveActiveUser",
        ]),
        ...mapMutations([
            "setActiveProject",
            "setLastEntryKey",
            "setStartTime",
            "setIsOnLunchBreak",
        ]),
        async restoreSession() {
            const entry = this.activeEntry;

            if (!entry) return; // nothing unfinished
            if (this.activeProject) return; // already restored

            const project = this.projects.find(
                (p) => p.projectId === entry.projectId
            );
            if (!project) return;

            const userId = this.user?.uid;
            const name = this.user?.name || "";
            const surname = this.user?.surname || "";

            // Push back into Vuex
            this.setActiveProject(project);
            this.setLastEntryKey(entry.uid);
            this.setStartTime(entry.startTime);

            // Ensure active user entry is recreated in DB
            await this.$store.dispatch("addRemoveActiveUser", {
                oldProject: null,
                newProject: project,
                userId,
                name,
                surname,
            });

            // Set status depending on project type
            const isLunch = project.name === "Lunch Break";
            const nextStatus = isLunch
                ? "onBreak"
                : entry?.overtime
                ? "inOvertime"
                : "loggedIn";
            this.setIsOnLunchBreak(isLunch);
            await this.updateUserStatus(nextStatus);
        },

        /**
         * Enables the edit mode for the description text area.
         * Sets the `isEditDescription` flag to `true` to display the editable input.
         */
        showEditDescription() {
            if (this.activeEntry) {
                this.descriptionText = this.activeEntry.description || "";
            } else if (this.userTimeEntryDescription) {
                this.descriptionText = this.userTimeEntryDescription;
            } else {
                this.descriptionText = "";
            }
            this.isEditDescription = true;
        },

        /**
         * Saves the description for the active time entry or the last known entry.
         *
         * - If there is an `activeEntry`, updates the description for that entry.
         * - Otherwise, updates the description for the last entry stored in the Vuex state.
         *
         * @async
         * @returns {Promise<void>} A promise that resolves once the description is successfully saved.
         * @throws {Error} If there is an issue updating the database.
         */
        async saveDescription(description) {
            const userId = this.user?.uid;
            const lastEntryKey = this.$store.state.lastEntryKey;

            if (this.activeEntry) {
                // Save description for the active entry
                await update(
                    ref(
                        db,
                        `users/${userId}/timeEntries/${this.activeEntry.uid}`
                    ),
                    {
                        description,
                    }
                );
            } else {
                // Save description for the last entry
                await update(
                    ref(db, `users/${userId}/timeEntries/${lastEntryKey}`),
                    {
                        description,
                    }
                );
            }
        },

        /**
         * Navigates to the user dashboard.
         */
        goToUserDashboard() {
            this.$router.push({ name: "UserDashboard" });
        },

        /**
         * Determines if a tooltip should be shown for a project.
         * @param {Object} project - The project object.
         * @returns {boolean} True if tooltip should be shown, false otherwise.
         */
        shouldShowTooltip(project) {
            return (
                project.activeUserCount > 0 &&
                this.rights?.canViewUserNamesInProjectList === true
            );
        },

        /**
         * Determines the color based on project priority.
         * @param {string} priority - Priority level of the project.
         * @returns {string} Corresponding color class.
         */
        getProjectPriorityColor(userPriority, globalPriority) {
            const priorityColors = {
                1: "deep-purple darken-1", // Priority 1 -> Red
                2: "error", // Priority 2 -> Orange Red
                3: "success", // Priority 3 -> Black
                4: "primary", // Priority 4 -> Dark Grey
                5: "text-primary", // Priority 5 -> Light Grey
            };

            // Special case for userPriority === 3
            if (userPriority === 3) {
                return priorityColors[globalPriority] || "primary"; // Fallback to black if globalPriority is invalid
            }

            // Default for other userPriority values
            return priorityColors[userPriority] || "primary"; // Fallback to light grey if userPriority is invalid
        },

        /**
         * Displays a snackbar with a message and optional action callback.
         * @param {string} message - Message to display.
         * @param {Function|null} action - Optional callback to execute on confirmation.
         */
        triggerSnackbar(message, action) {
            this.snackbarMessage = message;
            this.snackbarAction = action;
            this.snackbar = true;
        },

        /**
         * Handles end-of-day action by triggering a confirmation snackbar.
         */
        handleEndOfDay() {
            this.triggerSnackbar(
                "Are you sure you want to end the current work day?",
                this.confirmEndOfDay
            );
        },

        /**
         * Handles project button click - starts the project and clears tooltip if showing
         * @param {Object} project - The selected project object.
         * @param {Event} event - The click event.
         */
        handleProjectClick(project, event) {
            // If this is the immediate click after long press release, ignore it
            // The release of the long press triggers a click event, but we want to ignore that
            if (
                this.longPressProjectId === project.projectId &&
                this.longPressTouchEndTime
            ) {
                const timeSinceTouchEnd =
                    Date.now() - this.longPressTouchEndTime;
                // Ignore clicks within 300ms of touchend (the release click)
                if (timeSinceTouchEnd < 300) {
                    // Clear the touch end time so subsequent clicks work
                    this.longPressTouchEndTime = null;
                    return;
                }
            }

            // Clear long press state
            if (this.longPressProjectId === project.projectId) {
                this.longPressProjectId = null;
                this.longPressTouchEndTime = null;
            }

            // Wait for DOM update to ensure tooltip is hidden before showing snackbar
            this.$nextTick(() => {
                this.startProject(project);
            });
        },

        /**
         * Handles touch start for long press detection
         * @param {Object} project - The project being touched.
         * @param {Event} event - The touch event.
         */
        handleTouchStart(project, event) {
            // Only handle long press if user has permission to view user names
            if (
                !this.rights?.canViewUserNamesInProjectList ||
                project.activeUserCount === 0
            ) {
                return;
            }

            // Clear any existing timer
            if (this.longPressTimer) {
                clearTimeout(this.longPressTimer);
            }

            // Set timer for long press
            this.longPressTimer = setTimeout(() => {
                this.handleLongPress(project);
            }, 300);
        },

        /**
         * Handles touch end - cancels long press if it was a quick tap
         * @param {Object} project - The project that was touched.
         * @param {Event} event - The touch event.
         */
        handleTouchEnd(project, event) {
            // Clear the timer - if it fired, longPressProjectId will be set
            if (this.longPressTimer) {
                clearTimeout(this.longPressTimer);
                this.longPressTimer = null;
            }

            // If long press was triggered, record the touch end time to ignore the release click
            if (this.longPressProjectId === project.projectId) {
                // Record timestamp so we can ignore the immediate click (release click)
                this.longPressTouchEndTime = Date.now();

                // Hide tooltip after a delay to allow user to see it
                setTimeout(() => {
                    if (this.showTooltipForProject === project.projectId) {
                        this.showTooltipForProject = null;
                    }
                    // Clear longPressProjectId when tooltip hides so state is reset
                    if (this.longPressProjectId === project.projectId) {
                        this.longPressProjectId = null;
                        this.longPressTouchEndTime = null;
                    }
                }, 1000);
            }
        },

        /**
         * Handles touch cancel - clears long press timer
         */
        handleTouchCancel() {
            if (this.longPressTimer) {
                clearTimeout(this.longPressTimer);
                this.longPressTimer = null;
            }
            this.longPressProjectId = null;
            this.showTooltipForProject = null;
            this.longPressTouchEndTime = null;
        },

        /**
         * Handles long press - shows tooltip with active users
         * @param {Object} project - The project that was long pressed.
         */
        handleLongPress(project) {
            this.longPressProjectId = project.projectId;
            this.longPressTimer = null;
            // Show tooltip programmatically
            this.showTooltipForProject = project.projectId;
        },

        /**
         * Starts working on a selected project after confirmation.
         * @param {Object} project - The selected project object.
         */
        startProject(project) {
            this.selectedProject = project;
            this.triggerSnackbar(
                `Start  ${project.name}?`,
                this.confirmStartProject
            );
        },

        /**
         * Executes the action associated with the snackbar and closes it.
         */
        confirmAction() {
            if (this.snackbarAction) this.snackbarAction(); // Call the callback function if set
            this.snackbar = false; // Close the snackbar
        },

        /**
         * Confirms and processes the end-of-day action.
         * Ends active project, fetches updated user data, and navigates to the daily report.
         */
        async confirmEndOfDay() {
            if (this.isOnLunchBreak) {
                this.setIsOnLunchBreak(false);
            }

            const currentUser = auth.currentUser;
            const userId = currentUser.uid;
            if (!userId) return;

            const project = this.activeProject;
            const activeEntry = this.activeEntry || null;

            try {
                // 1) End the open time entry
                if (activeEntry) {
                    await this.$store.dispatch("endCurrentTimeEntry", {
                        isEndOfDay: true,
                        activeEntry,
                        userId,
                    });
                }

                // 2) Remove from activeUsers after the entry is closed
                if (project) {
                    await this.$store.dispatch("addRemoveActiveUser", {
                        oldProject: project,
                        newProject: null,
                        userId,
                        name: null,
                        surname: null,
                    });
                }

                // 3) Reset priorities for this user
                await this.$store.dispatch("resetUserProjectsPriorities");

                await this.updateUserStatus("endOfDay");

                // 4) Navigate
                this.$router.push({ name: "DailyReport" });
            } catch (err) {
                console.error("[EndOfDay] Failed:", err);
            }
        },

        /**
         * Confirms and starts a selected project.
         */
        async confirmStartProject() {
            // hard guard against double-tap
            if (this.disableProjectSelection) return;

            const currentUser = auth.currentUser;
            const userId = currentUser?.uid;
            if (!currentUser || !this.selectedProject?.projectId) {
                console.error("User or Project ID is missing.");
                return;
            }

            this.showCurrentProject = false;
            this.disableProjectSelection = true;

            try {
                // 1) End any existing active time entry
                const activeEntry = this.activeEntry;

                // capture the previous project
                const previousProject =
                    (this.activeProject && {
                        projectId: this.activeProject.projectId,
                    }) ||
                    (activeEntry && { projectId: activeEntry.projectId }) ||
                    null;

                if (activeEntry) {
                    await this.$store.dispatch("endCurrentTimeEntry", {
                        isEndOfDay: false,
                        activeEntry,
                        userId,
                    });
                }

                // 2) Immediately remove presence from the previous project in DB + local store
                if (previousProject) {
                    await this.$store.dispatch("addRemoveActiveUser", {
                        oldProject: previousProject,
                        newProject: null,
                        userId,
                        name: null,
                        surname: null,
                    });
                }

                const overtimeFlag =
                    this.selectedProject.projectId !== "SYSTEM_LUNCH-BREAK" &&
                    this.user?.userStatus === "inOvertime" &&
                    this.user?.overtimeToggleEnabled === true;

                // 3) Create a new time entry for the selected project
                const { key: lastEntryKey, startTime } =
                    await this.$store.dispatch("createTimeEntry", {
                        userId,
                        projectId: this.selectedProject.projectId,
                        overtime: overtimeFlag,
                    });
                if (!startTime) {
                    throw new Error(
                        "Failed to retrieve startTime from Firebase."
                    );
                }
                // 4) Commit that new project into Vuex
                if (this.selectedProject) {
                    this.setActiveProject(this.selectedProject);
                }
                if (startTime) {
                    this.setStartTime(startTime);
                }
                if (lastEntryKey) {
                    this.setLastEntryKey(lastEntryKey);
                }

                // 6) Add presence to the new project (action skips add for Lunch automatically)
                await this.$store.dispatch("addRemoveActiveUser", {
                    oldProject: null,
                    newProject: this.selectedProject,
                    userId,
                    name: this.user.name,
                    surname: this.user.surname,
                });
                // 6) Update user "on‐break" vs "logged‐in" status
                const isLunch =
                    this.selectedProject.projectId === "SYSTEM_LUNCH-BREAK";
                // Keep overtime status during lunch break - only toggle can change it
                const wasInOvertime = this.user?.userStatus === "inOvertime";
                let nextStatus;

                if (isLunch) {
                    nextStatus = wasInOvertime ? "inOvertime" : "onBreak";
                } else {
                    nextStatus = overtimeFlag ? "inOvertime" : "loggedIn";
                }
                this.setIsOnLunchBreak(isLunch);
                await this.updateUserStatus(nextStatus);

                // 7) Clean up UI flags
                this.isEditDescription = false;
                this.selectedProject = null;
            } catch (error) {
                console.error("Error switching projects:", error);
                this.triggerSnackbar(
                    "Failed to start the project. Please try again.",
                    null
                );
            } finally {
                this.disableProjectSelection = false;
            }
        },

        /**
         * Handle user toggling the overtime switch.
         *
         * Logic:
         * 1) If the current/active project is Lunch Break:
         *    - Force the toggle OFF (cannot be overtime on Lunch).
         *    - If user status is "inOvertime", switch it to "onBreak".
         *    - Exit early.
         *
         * 2) Otherwise, ensure we have a logged-in user. Then:
         *    - If toggling ON:
         *       a) Set user status to "inOvertime".
         *       b) If there is an active non-OT entry, restart the same project as an OT entry
         *          by leveraging the normal project-start flow (`confirmStartProject`).
         *    - If toggling OFF:
         *       a) Set user status to "loggedIn".
         *       b) If there is an active OT entry, end it without starting a new entry and
         *          remove presence from the project's activeUsers list.
         *
         * @returns {Promise<void>} Resolves once all updates complete.
         */
        async onOvertimeToggle(nextOn) {
            if (nextOn && !this.user?.overtimeToggleEnabled) return;

            const displayedProj = this.displayedProject?.project;
            const isLunch = displayedProj?.projectId === "SYSTEM_LUNCH-BREAK";

            // Don't allow toggling overtime while on lunch break
            // Overtime status is preserved during lunch and only changes via explicit toggle
            if (isLunch) {
                return;
            }

            const userId = this.user?.uid;
            if (!userId) return;

            try {
                const entry = this.activeEntry;
                const project = this.displayedProject?.project || null;

                if (nextOn) {
                    // Turning ON → status first, then (if needed) restart same project as overtime
                    await this.updateUserStatus("inOvertime");

                    if (entry && project && !entry.overtime) {
                        this.selectedProject = project;
                        await this.confirmStartProject();
                    }
                } else {
                    // Turning OFF → status first, then close current overtime entry (no new entry)
                    await this.updateUserStatus("loggedIn");

                    if (entry?.overtime && project) {
                        await this.$store.dispatch("endCurrentTimeEntry", {
                            isEndOfDay: false,
                            activeEntry: entry,
                            userId,
                        });
                        await this.$store.dispatch("addRemoveActiveUser", {
                            oldProject: { projectId: project.projectId },
                            newProject: null,
                            userId,
                            name: null,
                            surname: null,
                        });
                    }
                }
            } catch (err) {
                console.error("[OvertimeToggle] Failed:", err);
            }
        },

        /**
         * Starts a lunch break by ending the current project, creating a lunch_break entry, and updating user status.
         */
        async startLunchBreak() {
            const lunchBreakProject = this.projects.find(
                (project) => project.projectId === "SYSTEM_LUNCH-BREAK"
            );
            if (!lunchBreakProject) {
                return;
            }
            // Start the Lunch Break project like any other project
            this.startProject(lunchBreakProject);
        },
    },
};
</script>
<style scoped>
v-row {
    margin: 0 !important;
}

v-col {
    padding: 0 !important;
}

.project-button :deep(.v-btn__content) {
    width: 100%;
    min-width: 0;
    overflow: hidden;
}

.project-button-content {
    display: flex;
    justify-content: space-between;
    width: 100%;
    min-width: 0;
}
</style>